import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { API_URL } from "../utils/endpoints";

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());

  useEffect(() => {
    // Get auth token from localStorage
    const token = localStorage.getItem("authToken");

    // Create socket connection with authentication
    const newSocket = io(API_URL, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      auth: {
        token: token,
      },
      extraHeaders: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {},
    });

    newSocket.on("connect", () => {
      console.log("Socket.IO connected:", newSocket.id);
      setIsConnected(true);
      const currentToken = localStorage.getItem("authToken");
      if (currentToken) {
        newSocket.emit("authenticate", currentToken);
      }
      newSocket.emit("get:online_users");
    });

    const handleStorageChange = () => {
      const currentToken = localStorage.getItem("authToken");
      if (currentToken && newSocket.connected) {
        newSocket.auth = { token: currentToken };
        newSocket.emit("authenticate", currentToken);
      }
    };
    window.addEventListener("storage", handleStorageChange);

    newSocket.on("users:online_list", (ids) => {
      if (Array.isArray(ids)) {
        setOnlineUserIds(new Set(ids.map(String)));
      }
    });

    newSocket.on("user:status", ({ userId, isOnline }) => {
      if (!userId) return;
      const idStr = String(userId);
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        if (isOnline) {
          next.add(idStr);
        } else {
          next.delete(idStr);
        }
        return next;
      });
    });

    newSocket.on("disconnect", () => {
      console.log("Socket.IO disconnected");
      setIsConnected(false);
    });

    newSocket.on("connect_error", (error) => {
      console.error("Socket.IO connection error:", error);
      setIsConnected(false);
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      newSocket.close();
    };
  }, []);

  const isUserOnline = useCallback(
    (userId) => {
      if (!userId) return false;
      return onlineUserIds.has(String(userId));
    },
    [onlineUserIds]
  );

  const joinProject = (projectId) => {
    if (socket && projectId) {
      socket.emit("join-project", projectId);
      console.log(`Joined project room: ${projectId}`);
    }
  };

  const leaveProject = (projectId) => {
    if (socket && projectId) {
      socket.emit("leave-project", projectId);
      console.log(`Left project room: ${projectId}`);
    }
  };

  const value = {
    socket,
    isConnected,
    joinProject,
    leaveProject,
    onlineUserIds,
    isUserOnline,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
